import React, { useMemo } from "react";
import { inject, observer } from "mobx-react";

import Tooltip from "@material-ui/core/Tooltip";

import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import HistoryOutlinedIcon from "@material-ui/icons/HistoryOutlined";
import ArrowBackOutlinedIcon from "@material-ui/icons/ArrowBackOutlined";

import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import { RiPushpinFill, RiPushpin2Line } from "react-icons/ri";

import StyledIconButton from "app/Chat/buttons/StyledIconButton";

import { getTitle } from "app/common/utils/Logo";
import { cx, isTrue } from "./lib/utils";
import { makeStyles } from "@material-ui/core";

import { AppLogo } from "./icons";

const settingsEnabled = isTrue(
  process.env.REACT_APP_TIGERSHEET_ASSISANT_SETTINGS_ENABLED
);

const isDevEnv = process.env.NODE_ENV === "development";

const useStyles = makeStyles((theme) => ({
  headerRoot: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    justifyContent: "space-between",
    padding: "14px",
  },
  headerNav: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  headerTitleRoot: {
    width: "100%",
  },
  headerTitle: {
    fontSize: 16,
    margin: 0,
    color: (props) => props.themeColor || theme.palette.primary.main,
    marginBottom: 4,
  },
  headerSubTitle: {
    color: "rgba(0,0,0,.5)",
    fontWeight: 700,
    fontSize: 12,
  },
  appAboutStateInfoContainer: {
    display: "flex",
    alignItems: "center",
    gap: "0.25rem",
  },
  appAboutStateInfo: {
    backgroundColor: "rgba(0,0,0,.15)",
    color: "rgba(0,0,0,.5)",
    borderColor: "rgba(0,0,0,.1)",
    letterSpacing: "-0.01rem",
    textTransform: "uppercase",
    fontWeight: 700,
    borderRadius: "6px",
    padding: "1px 6px",
  },
  appIcon: {
    cursor: "auto !important",
    fill: (props) => props.themeColor || theme.palette.primary.main,
  },
  coloredAction: {
    color: (props) => props.themeColor || undefined,
  },
}));

const ChatDrawerHeader = ({
  themeColor,
  assistantConfig,
  onClose,
  //
  assistantStore,
  userStore,
  configStore,
}) => {
  const classes = useStyles({ themeColor });

  const appTitle = useMemo(() => <>{getTitle()} AI Assistant</>, []);

  const assistantAboutInfo = useMemo(() => {
    if (
      assistantConfig.about.stage === "release" ||
      assistantConfig.about.stage === "stable release"
    )
      return null;
    return (
      <div className={classes.appAboutStateInfoContainer}>
        <span className={classes.appAboutStateInfo}>
          {assistantConfig.about.stage}
        </span>
        <Tooltip title={assistantConfig.about.infoMessage}>
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </div>
    );
  }, []);

  const handleToggleDockState = () => {
    if (assistantStore.docked) {
      assistantStore.unDockChat();
    } else {
      assistantStore.dockChat();
    }
  };

  const handleGoBack = () => {
    assistantStore.changeView("CHAT_HOME");
  };

  const showSettings = () => {
    assistantStore.changeView("CHAT_SETTINGS");
  };

  const showChatHistory = () => {
    assistantStore.changeView("CHAT_HISTORY");
  };

  const renderChatSettingsAction = () => {
    const isAllowedAccount =
      configStore.config?.site?.server_url === "https://riktam.tigersheet.com" &&
      userStore.user?.email === "siddharam@riktamtech.com";
    // const isAllowedAccount =
    // configStore.config?.site?.server_url === "http://localhost/tigersheet/public" &&
    // userStore.user?.email === "bhawanishiv@gmail.com";
    /**
     * Render chat settings action based on these conditions:
     * 1. false, if view is already 'CHAT_SETTINGs'
     * 2. true, if NODE_ENV is 'development'
     * 3. true, if `settingsEnabled` is true
     * 4. true, if server_url is https://riktam.tigersheet.com and email is siddharam@riktamtech.com
     */
    if (
      assistantStore.currentView === "CHAT_SETTINGS" ||
      assistantStore.currentView !== "CHAT_HOME" ||
      (!isDevEnv && !settingsEnabled && !isAllowedAccount)
    )
      return null;

    return (
      <Tooltip title="Chat Settings">
        <div>
          <StyledIconButton onClick={showSettings}>
            <SettingsOutlinedIcon />
          </StyledIconButton>
        </div>
      </Tooltip>
    );
  };

  const renderChatHistoryAction = () => {
    if (
      assistantStore.currentView === "CHAT_HISTORY" ||
      assistantStore.currentView !== "CHAT_HOME"
    )
      return null;

    return (
      <Tooltip title="Chat History">
        <div>
          <StyledIconButton onClick={showChatHistory}>
            <HistoryOutlinedIcon />
          </StyledIconButton>
        </div>
      </Tooltip>
    );
  };

  const renderCloseAction = () => {
    return (
      <Tooltip title={assistantStore.docked ? "" : "Close"}>
        <div style={{ position: "relative" }}>
          <StyledIconButton
            color="inherit"
            onClick={onClose}
            aria-label="close"
            disabled={assistantStore.docked}
          >
            <KeyboardArrowRightIcon
              style={{
                opacity: assistantStore.docked ? ".3" : "1",
              }}
            />
          </StyledIconButton>
        </div>
      </Tooltip>
    );
  };

  const renderDockAction = () => {
    return (
      <Tooltip title={assistantStore.docked ? "Undock" : "Dock"}>
        <div style={{ position: "relative" }}>
          <StyledIconButton
            color={assistantStore.docked ? "primary" : "default"}
            onClick={handleToggleDockState}
            className={cx(assistantStore.docked && classes.coloredAction)}
          >
            {assistantStore.docked ? <RiPushpinFill /> : <RiPushpin2Line />}
          </StyledIconButton>
        </div>
      </Tooltip>
    );
  };

  const renderAppInfo = () => {
    return (
      <>
        <AppLogo color="primary" fontSize="large" className={classes.appIcon} />
        <div className={classes.headerTitleRoot}>
          <h2 className={classes.headerTitle}>{appTitle}</h2>
          {assistantAboutInfo}
        </div>
      </>
    );
  };

  const renderBackAction = () => {
    return (
      <Tooltip title="Go back">
        <div style={{ position: "relative" }}>
          <StyledIconButton onClick={handleGoBack}>
            <ArrowBackOutlinedIcon />
          </StyledIconButton>
        </div>
      </Tooltip>
    );
  };

  const renderHeaderTitle = () => {
    switch (assistantStore.currentView) {
      case "CHAT_SETTINGS": {
        return (
          <>
            {renderBackAction()}
            <div className={classes.headerTitleRoot}>
              <h2 className={classes.headerTitle}>Chat Settings</h2>
              <div className={classes.headerSubTitle}>{appTitle}</div>
            </div>
          </>
        );
      }

      case "CHAT_HISTORY": {
        return (
          <>
            {renderBackAction()}
            <div className={classes.headerTitleRoot}>
              <h2 className={classes.headerTitle}>Chat History</h2>
              <div className={classes.headerSubTitle}>{appTitle}</div>
            </div>
          </>
        );
      }

      case "CHAT_HOME":
      default: {
        return renderAppInfo();
      }
    }
  };

  const renderChatDrawerHeader = () => {
    return (
      <div className={classes.headerRoot}>
        {renderHeaderTitle()}
        <div className={classes.headerNav}>
          {renderChatSettingsAction()}
          {renderChatHistoryAction()}
          {renderDockAction()}
          {renderCloseAction()}
        </div>
      </div>
    );
  };

  return renderChatDrawerHeader();
};

export default inject(({ store }) => {
  return {
    assistantStore: store.assistantStore,
    userStore: store.userStore,
    configStore: store.configStore,
  };
})(observer(ChatDrawerHeader));
